/**
 * Servicio para integración con Verestro
 * Maneja la creación de tarjetas MasterCard para usuarios INDIVIDUAL
 */

import { getServerUrl } from '../../config/server.config';

import { authService } from '../auth.service';

export interface VerestroWalletResponse {
  success: boolean;
  message: string;
  data: {
    integrationId: string;
    verestroUserId: string;
    verestroExternalId: string;
    status: string;
    kycStatus: string;
    nextStep: string;
  };
}

export interface VerestroTermsResponse {
  success: boolean;
  message: string;
  data: {
    termsAccepted: boolean;
    termsVersion: string;
    termsAcceptedAt: string;
    nextStep: string;
  };
}

export interface VerestroVerificationResponse {
  success: boolean;
  message: string;
  data: {
    verificationId: string;
    kycStatus: string;
    nextStep: string;
  };
}

export interface VerestroBalanceResponse {
  success: boolean;
  message: string;
  data: {
    balanceId: string;
    currency: string;
    nextStep: string;
  };
}

export interface VerestroCardResponse {
  success: boolean;
  message: string;
  data: {
    cardId: string;
    lastFourDigits: string;
    expiryDate: string;
    status: string;
    currency: string;
  };
}

export interface VerestroCardDetails {
  id: string;
  verestroCardId: string;
  lastFourDigits: string;
  expiryDate: string;
  status: string;
  currency: string;
  balance?: number;
}

class VerestroService {
  private get baseUrl(): string {
    const serverUrl = getServerUrl(true);
    return `${serverUrl}/providers/verestro`;
  }

  /**
   * PASO 1: Crear wallet
   */
  async createWallet(): Promise<VerestroWalletResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/wallet`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create wallet');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating Verestro wallet:', error);
      throw error;
    }
  }

  /**
   * PASO 1.5: Aceptar términos y condiciones
   */
  async acceptTermsAndConditions(accepted: boolean = true, termsVersion: string = '1.0'): Promise<VerestroTermsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/accept-terms`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accepted, termsVersion })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to accept terms');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  }

  /**
   * PASO 2: Crear verificación KYC
   * Los datos se toman automáticamente del onboarding INDIVIDUAL en el backend
   */
  async createVerification(): Promise<VerestroVerificationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/verification`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create verification');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating verification:', error);
      throw error;
    }
  }

  /**
   * PASO 3: Crear balance
   * NOTA: Verestro solo soporta EUR para tarjetas
   */
  async createBalance(): Promise<VerestroBalanceResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/balance`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          currency: 'EUR' // Hardcodeado - solo EUR soportado
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create balance');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating balance:', error);
      throw error;
    }
  }

  /**
   * PASO 4: Crear tarjeta
   */
  async createCard(balanceId: string, cardName?: string): Promise<VerestroCardResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/card`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balanceId, cardName })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create card');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error creating card:', error);
      throw error;
    }
  }

  /**
   * Obtener todas las tarjetas del usuario
   */
  async getUserCards(): Promise<{ success: boolean; data: VerestroCardDetails[] }> {
    try {
      const response = await fetch(`${this.baseUrl}/cards`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch cards');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching user cards:', error);
      throw error;
    }
  }

  /**
   * Obtener detalles de una tarjeta específica
   */
  async getCardDetails(cardId: string): Promise<{ success: boolean; data: VerestroCardDetails }> {
    try {
      const response = await fetch(`${this.baseUrl}/cards/${cardId}`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch card details');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching card details:', error);
      throw error;
    }
  }

  /**
   * Obtener balance de una tarjeta
   */
  async getCardBalance(cardId: string): Promise<{ success: boolean; data: { balance: number; currency: string } }> {
    try {
      const response = await fetch(`${this.baseUrl}/cards/${cardId}/balance`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch balance');
      }

      const data = await response.json();
      
      // Manejar la respuesta según la estructura del backend
      if (data && data.success) {
        return data;
      }
      
      // Si la respuesta viene directamente con balance
      return {
        success: true,
        data: {
          balance: data.balance || 0,
          currency: data.currency || 'EUR'
        }
      };
    } catch (error: any) {
      console.error('Error fetching card balance:', error);
      throw error;
    }
  }

  /**
   * Bloquear/Desbloquear tarjeta
   */
  async toggleCardBlock(cardId: string, action: 'block' | 'unblock'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/cards/${cardId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle card block');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error toggling card block:', error);
      throw error;
    }
  }

  /**
   * Eliminar tarjeta
   */
  async deleteCard(cardId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/cards/${cardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete card');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error deleting card:', error);
      throw error;
    }
  }

  /**
   * Flujo completo de creación de tarjeta
   * Ejecuta todos los pasos en secuencia
   */
  async createMasterCardComplete(
    onProgress?: (step: number, message: string) => void
  ): Promise<VerestroCardResponse> {
    try {
      // PASO 1: Crear wallet
      onProgress?.(1, 'Creating wallet...');
      const walletResponse = await this.createWallet();
      
      if (!walletResponse.success) {
        throw new Error(walletResponse.message || 'Failed to create wallet');
      }

      // PASO 2: Aceptar términos (se manejará con modal)
      // Este paso se hará después de mostrar el modal al usuario
      
      // PASO 3: Crear verificación KYC
      onProgress?.(3, 'Verifying identity...');
      const verificationResponse = await this.createVerification();
      
      if (!verificationResponse.success) {
        throw new Error(verificationResponse.message || 'Failed to create verification');
      }

      // PASO 4: Crear balance (siempre EUR)
      onProgress?.(4, 'Creating EUR balance...');
      const balanceResponse = await this.createBalance();
      
      if (!balanceResponse.success) {
        throw new Error(balanceResponse.message || 'Failed to create balance');
      }

      // PASO 5: Crear tarjeta
      onProgress?.(5, 'Creating MasterCard...');
      const cardResponse = await this.createCard(balanceResponse.data.balanceId);
      
      if (!cardResponse.success) {
        throw new Error(cardResponse.message || 'Failed to create card');
      }

      onProgress?.(6, 'MasterCard created successfully!');
      return cardResponse;

    } catch (error: any) {
      console.error('Error in MasterCard creation flow:', error);
      throw error;
    }
  }
}

export const verestroService = new VerestroService();